import { WEB_AGENT_ACTION_TYPE, type WebAgentSpec, type WebAgentStep } from './webAgentTypes';

type AnyAction = Record<string, any> | undefined | null;

function agentAttrsOf(agent: AnyAction): Record<string, any> {
  const map: Record<string, any> = {};
  for (const item of (agent as any)?.aiworkers_attrs ?? []) {
    if (item?.attr_name) map[item.attr_name] = item.attr_json ?? item.attr_value;
  }
  return map;
}

export function isWebAgentAgent(agent: AnyAction): boolean {
  return Boolean(agentAttrsOf(agent).web_agent_spec) || (agent as any)?.sub_type === WEB_AGENT_ACTION_TYPE;
}

export function readWebAgentStartUrl(raw: any): string {
  let value: any = raw;
  if (typeof value === 'string') {
    try { value = JSON.parse(value); } catch { return ''; }
  }
  return value && typeof value === 'object' && typeof value.startUrl === 'string' ? value.startUrl : '';
}

export function getWebAgentStartUrl(agent: AnyAction): string {
  return readWebAgentStartUrl(agentAttrsOf(agent).web_agent_spec);
}

/** Array-valued groups under config.instructions_data. */
export function parseInstructionsGroups(config: any): Record<string, any[]> {
  let cfg: any = config;
  if (typeof cfg === 'string') {
    try { cfg = JSON.parse(cfg); } catch { cfg = null; }
  }
  const inst = cfg && typeof cfg === 'object' ? cfg.instructions_data : undefined;
  const obj = inst && typeof inst === 'object' ? inst : {};
  const out: Record<string, any[]> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (Array.isArray(value)) out[key] = value;
  }
  return out;
}

/** Configuration-management query for a web agent's active `agent_config` (its steps). */
export function buildActiveWebAgentConfigQuery(aiworkerId: string) {
  return {
    scope: 'tenant',
    pagination: { page: 1, per_page: 1 },
    filters: { aiworker_id: aiworkerId, config_name: 'agent_config', status: 'active', is_active: true },
  };
}

/** Runnable spec: start URL from the agent's `web_agent_spec` attr, steps/custom from its active config. */
export function buildWebAgentSpec(agent: AnyAction, config: unknown): WebAgentSpec {
  const groups = parseInstructionsGroups(config);
  return { startUrl: getWebAgentStartUrl(agent), steps: groups.steps ?? [], custom: groups.custom ?? [] };
}

/**
 * Rewrite step ids to `step-1`…`step-N` in list order and remap `skipWhenStepId` to the new ids.
 * Dependencies that would point at self/later/missing steps are cleared.
 */
export function renumberWebAgentSteps(steps: WebAgentStep[]): WebAgentStep[] {
  if (!Array.isArray(steps) || steps.length === 0) return Array.isArray(steps) ? steps : [];
  const idMap = new Map<string, string>();
  steps.forEach((step, i) => {
    const oldId = typeof step?.id === 'string' ? step.id.trim() : '';
    if (oldId) idMap.set(oldId, `step-${i + 1}`);
  });
  return steps.map((step, i) => {
    const next: WebAgentStep = { ...step, id: `step-${i + 1}` };
    const dep = typeof step?.skipWhenStepId === 'string' ? step.skipWhenStepId.trim() : '';
    const mapped = dep ? idMap.get(dep) : undefined;
    const mappedIndex = mapped ? Number.parseInt(mapped.replace(/^step-/, ''), 10) - 1 : -1;
    if (mapped && Number.isFinite(mappedIndex) && mappedIndex >= 0 && mappedIndex < i) {
      next.skipWhenStepId = mapped;
    } else {
      delete (next as { skipWhenStepId?: string }).skipWhenStepId;
    }
    const follows = typeof step?.dependsOnStepId === 'string' ? step.dependsOnStepId.trim() : '';
    const followsMapped = follows ? idMap.get(follows) : undefined;
    if (followsMapped && followsMapped !== next.id) next.dependsOnStepId = followsMapped;
    else delete (next as { dependsOnStepId?: string }).dependsOnStepId;
    return next;
  });
}
